import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HrPageShell } from './HrPageShell';
import { EmployeeAvatar, EmployeeLink, StatusBadge, EmptyState, ProgressBar } from './hr-ui';
import { useEmployees, useOnboardingTasks } from '../hr-store';
import { useDeleteTaskMutation, useUpsertTaskMutation } from '@/client/api/hr/people';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { CHECKLIST_STATUS_BADGE, CHECKLIST_STATUS_LABEL, ONBOARDING_CATEGORY_LABEL, type ChecklistTaskStatus, type OnboardingCategory } from '@/client/types/hr';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const STATUSES: ChecklistTaskStatus[] = ['not_started', 'in_progress', 'completed'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

interface ChecklistBoardProps {
  kind: 'onboarding' | 'offboarding';
  title: string;
  description: string;
  crumbLabel: string;
  icon: LucideIcon;
}

export function ChecklistBoard({ kind, title, description, crumbLabel, icon }: ChecklistBoardProps) {
  const employees = useEmployees();
  const tasks = useOnboardingTasks();
  const upsertTask = useUpsertTaskMutation();
  const deleteTask = useDeleteTaskMutation();
  const canManage = useGate(PERMISSIONS.hr.manage).allowed;
  const failed = (err: unknown) => toast.error((err as Error).message || 'Something went wrong');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [category, setCategory] = useState<OnboardingCategory>('paperwork');
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState('');

  const kindTasks = useMemo(() => tasks.filter((t) => t.kind === kind), [tasks, kind]);
  const employeeIds = useMemo(() => [...new Set(kindTasks.map((t) => t.employeeId))], [kindTasks]);

  const stats = useMemo(() => {
    const completed = kindTasks.filter((t) => t.status === 'completed').length;
    return [
      { key: 'employees', label: 'Employees tracked', value: employeeIds.length },
      { key: 'tasks', label: 'Total tasks', value: kindTasks.length },
      { key: 'completed', label: 'Completed', value: completed },
      { key: 'open', label: 'Open', value: kindTasks.length - completed },
    ];
  }, [kindTasks, employeeIds]);

  const employeeName = (id?: string | null) => employees.find((e) => e.id === id)?.name ?? '—';

  const openAdd = () => {
    setEmployeeId(employeeIds[0] ?? employees[0]?.id ?? '');
    setTaskTitle('');
    setCategory('paperwork');
    setAssigneeId('');
    setDueDate('');
    setDialogOpen(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeId || !taskTitle.trim()) {
      toast.error('Employee and task title are required');
      return;
    }
    upsertTask.mutate(
      {
        employeeId,
        kind,
        title: taskTitle.trim(),
        category,
        status: 'not_started',
        dueDate: dueDate || undefined,
        assigneeId: assigneeId || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Task added');
          setDialogOpen(false);
        },
        onError: failed,
      },
    );
  };

  return (
    <HrPageShell
      mock={false}
      title={title}
      description={description}
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Onboarding & Offboarding' }, { label: crumbLabel }]}
      icon={icon}
      actions={
        <PermissionButton code={PERMISSIONS.hr.manage} className="gap-2" onClick={openAdd}>
          <Plus className="h-4 w-4" /> Add task
        </PermissionButton>
      }
      stats={stats}
    >
      {employeeIds.length === 0 ? (
        <EmptyState label={`No ${kind} checklists yet.`} />
      ) : (
        <div className="space-y-4">
          {employeeIds.map((id) => {
            const employee = employees.find((e) => e.id === id);
            const employeeTasks = kindTasks.filter((t) => t.employeeId === id);
            const completedCount = employeeTasks.filter((t) => t.status === 'completed').length;
            const progress = employeeTasks.length ? (completedCount / employeeTasks.length) * 100 : 0;
            return (
              <div key={id} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-3">
                  <EmployeeAvatar employee={employee} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">
                      <EmployeeLink employee={employee} />
                    </p>
                    <p className="text-xs text-gray-400">
                      {employee?.title} · {completedCount}/{employeeTasks.length} complete
                    </p>
                  </div>
                  <ProgressBar value={progress} className="w-32" />
                </div>
                <div className="divide-y divide-gray-50">
                  {employeeTasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-gray-800">{t.title}</p>
                        <p className="text-[11px] text-gray-400">
                          {ONBOARDING_CATEGORY_LABEL[t.category]}
                          {t.assigneeId && ` · Assigned to ${employeeName(t.assigneeId)}`}
                          {t.dueDate && ` · Due ${new Date(t.dueDate).toLocaleDateString()}`}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button" disabled={!canManage}>
                            <StatusBadge label={CHECKLIST_STATUS_LABEL[t.status]} className={`${CHECKLIST_STATUS_BADGE[t.status]} cursor-pointer`} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {STATUSES.map((s) => (
                            <DropdownMenuItem
                              key={s}
                              onClick={() => {
                                upsertTask.mutate(
                                  { id: t.id, status: s },
                                  { onSuccess: () => toast.success(`Marked as ${CHECKLIST_STATUS_LABEL[s]}`), onError: failed },
                                );
                              }}
                            >
                              {CHECKLIST_STATUS_LABEL[s]}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <button
                        type="button"
                        onClick={() => {
                          deleteTask.mutate(t.id, { onSuccess: () => toast.success('Task removed'), onError: failed });
                        }}
                        className={`text-gray-400 hover:text-red-600 ${canManage ? '' : 'hidden'}`}
                        aria-label="Delete task"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add {kind} task</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Employee">
              <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={inputClass}>
                <option value="">Select an employee</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Task">
              <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} className={inputClass} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Category">
                <select value={category} onChange={(e) => setCategory(e.target.value as OnboardingCategory)} className={inputClass}>
                  {Object.entries(ONBOARDING_CATEGORY_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Assignee">
                <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={inputClass}>
                  <option value="">Unassigned</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Due date">
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputClass} />
              </Field>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Add task</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </HrPageShell>
  );
}
